/**
 * Qui reçoit les remontées d'incident — réservé à l'administrateur.
 *
 * *Décision de Chris, 21/09/2026 : « toutes les remontées d'incident doivent être vues par
 * assistantes et responsable SAV ; en réalité c'est l'assistante qui prendra en compte mais le
 * responsable SAV verra aussi. »*
 *
 * ## Pourquoi ici, et non au référentiel de main-d'œuvre
 *
 * Les fonctions d'un chantier se cochent au référentiel MO (018), parce qu'un code d'ERP écrit en
 * dur serait un fork qui commence. Cette règle ne s'applique pas ici, et **la mesure l'interdit** :
 * relevé le 21/09/2026, le code `_MOIAT` (« MOI ATELIER ») porte à la fois le Resp Pôle Matériel
 * et les quatre mécaniciens. Aucune règle posée sur le code ne saurait les distinguer — et ce
 * n'est pas ce que le code décrit : il porte un coût horaire, pas une responsabilité.
 *
 * C'est exactement le constat du 15/08/2026 qui avait fait naître la table `responsable`, vérifié
 * une seconde fois sur un autre code.
 *
 * ## Plusieurs par rôle, contrairement aux responsables de validation
 *
 * Chris dit « **les** assistantes ». `responsable` tient un seul titulaire par fonction, et c'est
 * juste pour les circuits de validation — une contre-étude ne peut pas être acceptée par deux
 * personnes. Ici, la pile est partagée : plusieurs assistantes, plusieurs responsables si besoin,
 * et **tous voient tout**. Le rôle dit qui est censé agir, il ne filtre rien.
 *
 * ## Une pile vide se voit
 *
 * Tant que personne n'est posé, les déclarations s'écrivent et ne sont montrées à personne. C'est
 * la fonction muette de la 059, et elle a coûté deux fois — l'écran le dit donc en clair, ici et
 * dans la pile elle-même.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { chargerDestinatairesSav, poserDestinataireSav, retirerDestinataireSav } from "./api";
import type { Compte, DestinataireSav } from "./api";

const ROLES: readonly { readonly code: "assistante" | "responsable"; readonly libelle: string; readonly aide: string }[] =
  [
    {
      code: "assistante",
      libelle: "Assistantes SAV",
      aide: "Elles prennent en compte les remontées et les qualifient.",
    },
    {
      code: "responsable",
      libelle: "Responsable SAV",
      aide: "Il voit tout, et peut qualifier lui aussi.",
    },
  ];

export function DestinatairesSav({ membres }: { membres: readonly Compte[] }): JSX.Element {
  const [destinataires, setDestinataires] = useState<readonly DestinataireSav[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const recharger = (): void => {
    chargerDestinatairesSav()
      .then((r) => {
        setDestinataires(r.destinataires);
        setErreur(null);
      })
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      });
  };

  useEffect(recharger, []);

  const agir = (promesse: Promise<unknown>, cle: string): void => {
    setOccupe(cle);
    setErreur(null);
    promesse
      .then(recharger)
      .catch((x: unknown) => {
        setErreur(x instanceof Error ? x.message : String(x));
      })
      .finally(() => {
        setOccupe(null);
      });
  };

  // Un compte suspendu ne reçoit plus rien : le proposer ferait envoyer des remontées dans le vide.
  const nommables = membres.filter((m) => m.statut === "actif");

  return (
    <section className="referentiel">
      <h3>Qui reçoit les remontées d&apos;incident</h3>
      <p className="etat">
        Assistantes et responsable SAV reçoivent <strong>toutes</strong> les remontées, sans
        aiguillage. Tant que personne n&apos;est désigné, les déclarations s&apos;enregistrent mais
        personne ne les voit.
      </p>

      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}

      {destinataires === null ? (
        <p className="etat">Chargement…</p>
      ) : (
        <>
          {destinataires.length === 0 && (
            <p className="alerte">
              Personne ne reçoit les remontées d&apos;incident aujourd&apos;hui.
            </p>
          )}
          <table>
            <thead>
              <tr>
                <th>Rôle</th>
                <th>Désignés</th>
                <th>Ajouter</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((r) => {
                const tenus = destinataires.filter((d) => d.role === r.code);
                return (
                  <tr key={r.code}>
                    <td>
                      {r.libelle}
                      <br />
                      <span className="etat">{r.aide}</span>
                    </td>
                    <td>
                      {tenus.length === 0 ? (
                        <span className="en-ecart">Personne</span>
                      ) : (
                        <ul>
                          {tenus.map((d) => (
                            <li key={d.utilisateurId}>
                              {d.prenom} {d.nom}
                              {d.statut !== "actif" && (
                                <span className="en-ecart"> · compte {d.statut}</span>
                              )}{" "}
                              <button
                                type="button"
                                className="lien"
                                disabled={occupe !== null}
                                onClick={() => {
                                  agir(
                                    retirerDestinataireSav(d.utilisateurId, r.code),
                                    `${d.utilisateurId}-${r.code}`,
                                  );
                                }}
                              >
                                retirer
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>
                      <select
                        value=""
                        disabled={occupe !== null}
                        onChange={(e) => {
                          if (e.target.value === "") return;
                          agir(poserDestinataireSav(e.target.value, r.code), r.code);
                        }}
                      >
                        <option value="">— choisir —</option>
                        {nommables
                          .filter((m) => !tenus.some((d) => d.utilisateurId === m.id))
                          .map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.prenom} {m.nom}
                            </option>
                          ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
